import { skipToken } from "@reduxjs/toolkit/query";
import type { TrainingParticipantChapterAssessment } from "@repo/app-types/entities/trainingParticipant";
import type { TrainingParticipantChapterGradingId } from "@repo/app-types/entities/trainingParticipantChapterGrading";
import { Size } from "@repo/ui/helpers/helpers";
import type { FC } from "react";
import { Header, Heading } from "react-aria-components";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useTrainingParticipantChapterGradingsQuery } from "../../api/entities/trainingParticipant.js";
import { useChapterPassRate } from "./chapterAssessmentHelpers.js";
import { ChapterAssessmentTable } from "./chapterAssessmentTable.js";
import { ChapterPassRateTrend } from "./chapterPassRateTrend.js";
import { TrainingAssessmentTemplateScorePill } from "./trainingAssessmentTemplateScorePill.js";

export interface ChapterAssessmentDetailsProps {
  projectId: string;
  participantId: string;
  chapterId: string;
  /**
   * The latest chapter assessment from the participant's interview metadata.
   * Used as a fallback for legacy participants whose grading history table
   * is empty.
   */
  assessment: TrainingParticipantChapterAssessment | undefined;
  /** `null` means "current grade" (most recent grading); otherwise a specific historical grading id. */
  selectedGradingId: TrainingParticipantChapterGradingId | null;
}

export const ChapterAssessmentDetails: FC<ChapterAssessmentDetailsProps> = (
  props,
) => {
  const { data: gradingHistory } = useTrainingParticipantChapterGradingsQuery({
    projectId: props.projectId,
    participantId: props.participantId,
    chapterId: props.chapterId,
  });

  // `null` selectedGradingId means "current grade" — i.e. the newest entry.
  const selectedIndex =
    props.selectedGradingId === null
      ? 0
      : (gradingHistory?.findIndex((g) => g.id === props.selectedGradingId) ??
        -1);
  const selectedGrading =
    selectedIndex >= 0 ? gradingHistory?.[selectedIndex] : undefined;
  // The grading immediately older than the selected one. Used to compute the
  // pass-rate trend; undefined when the selected entry is already the oldest
  // (or when history hasn't loaded yet).
  const previousGrading =
    selectedIndex >= 0 ? gradingHistory?.[selectedIndex + 1] : undefined;

  // When we have a selected history entry, drive the display off of it.
  // Otherwise (legacy participants, or before history loads) fall back to the
  // assessment that lives on the interview metadata.
  const displayedAssessment = selectedGrading?.grading.assessment_template
    ? {
        assessment_template: selectedGrading.grading.assessment_template,
      }
    : props.assessment;

  const playbookId =
    displayedAssessment?.assessment_template?.recording_assessment_template_id;
  const criteriaGrading =
    displayedAssessment?.assessment_template?.criteria_grading;
  const { data: playbook, isLoading: isLoadingPlaybook } = usePlaybookByIdQuery(
    playbookId ?? skipToken,
  );

  const passRate = useChapterPassRate(playbook, criteriaGrading);
  const previousPassRate = useChapterPassRate(
    playbook,
    previousGrading?.grading.assessment_template?.criteria_grading,
  );

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <Header className="flex items-center gap-3">
          <Heading level={4} className="text-sm font-semibold text-foreground">
            Playbook preparedness
          </Heading>
          <div className="flex items-center gap-1.5">
            <TrainingAssessmentTemplateScorePill
              size={Size.SMALL}
              score={passRate}
              isLoading={isLoadingPlaybook}
            />
            <ChapterPassRateTrend
              currentPassRate={passRate}
              previousPassRate={previousPassRate}
            />
          </div>
        </Header>

        {playbook && criteriaGrading ? (
          <ChapterAssessmentTable
            playbook={playbook}
            criteriaGradings={criteriaGrading}
          />
        ) : null}
      </section>
    </div>
  );
};
